import { useState } from 'react';
import { sitePaymentCopy } from '@evi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Zap } from 'lucide-react';
import { api, getDisplayErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { TermsGate } from '@/components/TermsGate';
import { BankWindowText, type ReimbursementInfo } from '@/components/site/BankSetupBanner';
import styles from './SitePages.module.css';
import { CanadianPayoutNote } from '@/components/payouts/CanadianPayoutNote';
import { bankTimingKeys, payoutLocale, type DepositSchedule } from '@/lib/sitePayouts';
import { fillPayoutCopy, payoutSetupCopy, splitLinkTemplate } from '@/lib/payoutSetupCopy';
import { locationBankCardView } from '@/lib/locationBankCardView';

interface BankStatus {
  has_account: boolean; ready: boolean; status: string;
  checked_at: string | null; currently_due: string[];
  reimbursement?: ReimbursementInfo;
  /** Absent on an older Api. */
  deposit_schedule?: DepositSchedule;
  /** True when an active site_payee_links row holds the location's payee: the account is that linked payee's own. Whose it is
   *  depends on the site: at a split location (one with site_host_electricity below) it is the charger owner's legacy share
   *  payee; elsewhere it is the site host's own. Absent on an older Api. */
  linked_share_payee?: boolean;
  /** False when the Api refuses to open Stripe onboarding on this account; the card then offers no setup. Absent on an older Api. */
  can_open_onboarding?: boolean;
  /** The site host's own electricity payee, when one is recorded (option B). Null when there is none; absent on an older Api. */
  site_host_electricity?: { has_account: boolean; ready: boolean; status: string; currently_due: string[]; checked_at: string | null } | null;
}

export function LocationBankCard({ locationId, compact = false }: { locationId: string; compact?: boolean }) {
  const userId = useAuthStore((state) => state.user?._id);
  const queryClient = useQueryClient();
  const [country, setCountry] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const query = useQuery({ queryKey: ['location-bank', userId, locationId], queryFn: async () =>
    (await api.get(`locations/${locationId}/connect/status`).json<{ data: BankStatus }>()).data,
  staleTime: 30_000 });
  const status = query.data;
  const payment = sitePaymentCopy(payoutLocale());
  const text = payoutSetupCopy(payoutLocale());
  const [warningBefore, warningAfter] = splitLinkTemplate(text.locationCountryWarning);
  const timing = bankTimingKeys(status);
  // Gate on what the Api allows: no setup where it refuses onboarding; the charger owner is named only at a split location.
  const view = locationBankCardView(status);
  async function connect() {
    setBusy(true); setError('');
    try {
      const response = await api.post(`locations/${locationId}/connect/start`, { json: {
        authorized, ...(!status?.has_account ? { country } : {}),
      } }).json<{ data: { url: string } }>();
      window.location.assign(response.data.url);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, text.locationOpenFailed));
      setBusy(false);
      // A 409 for unaccepted terms (payout, or the Site Host Terms for a newly
      // assigned location) means the gate's cached status is stale: refetch it
      // so the accept card appears instead of only an error line.
      void queryClient.invalidateQueries({ queryKey: ['terms', 'status'] });
    }
  }
  if (compact && status?.reimbursement?.applicable === false) return null;
  return <section id={compact ? undefined : 'location-bank'} className={`${styles.bankCard} ${compact ? '' : styles.card}`} aria-label={payment.bankHeading}>
    {/* The server omits site_host_terms until it is published, and for anyone
        who is not this site's Building Admin or Property Manager, so before the
        flip this gate asks for the Payout Terms only. */}
    {authorized && <TermsGate docs={['payout_terms', 'site_host_terms']} />}
    <div className={styles.cardHead}><Landmark size={18} aria-hidden /><h2 className={styles.cardTitle}>{view.heading ? text[view.heading] : payment.bankHeading}</h2></div>
    {!compact && <CanadianPayoutNote locationId={locationId} />}
    {query.isPending ? <p>{text.locationChecking}</p> : query.isError ? <p className={styles.error} role="alert">{text.locationCheckFailed} <button onClick={() => void query.refetch()}>{payment.retry}</button></p> : <>
      <p className={status?.ready ? styles.ok : styles.warn}>{text[view.line]}</p>
      {view.note && <p className={styles.muted}>{text[view.note]}</p>}
      {status?.reimbursement?.applicable && !status.ready && <p><BankWindowText info={status.reimbursement} /></p>}
      {status?.reimbursement?.balances?.map(b => <p key={`${b.currency}-${b.status}`}>
        {fillPayoutCopy(text.balanceLine, { label: b.status === 'held' ? text.balanceHeld : b.status === 'payable' ? text.balancePayable : b.status === 'not_payable' ? text.balanceNotPayable : text.balanceReview, amount: b.currency === 'unknown' ? text.balanceUnknownAmount : new Intl.NumberFormat(undefined, { style: 'currency', currency: b.currency }).format(b.amount_cents / 100) })}
      </p>)}
      {timing.length > 0 && <p className={styles.muted}>{timing.map(key => payment[key]).join(' ')}</p>}
      {view.siteHost && <div className={styles.payeeBlock}>
        <div className={styles.cardHead}><Zap size={18} aria-hidden /><h3 className={styles.cardTitle}>{text.siteHostHeading}</h3></div>
        <p className={view.siteHostReady ? styles.ok : styles.warn}>{text[view.siteHost]}</p>
      </div>}
      {compact ? <a href={`/site-settings?site=${locationId}`}>{text.locationManage}</a> : <>
        {view.setup && <>
        {!status?.has_account && <label className={styles.field}>{text.locationBankCountry}
          <select className={styles.select} value={country} onChange={(event) => setCountry(event.target.value)}>
            <option value="">{text.chooseCountry}</option><option value="US">{text.countryUS}</option><option value="CA">{text.countryCA}</option>
          </select>
        </label>}
        {!status?.has_account && <p className={styles.muted}>{warningBefore}<a href="mailto:support@evinitiative.com">{text.contactSupportAt}</a>{warningAfter}</p>}
        <label className={styles.field}><input type="checkbox" checked={authorized} onChange={(event) => setAuthorized(event.target.checked)} />{text.locationAuthorized}</label>
        <button type="button" className={styles.primary} disabled={busy || !authorized || (!status?.has_account && !country)} onClick={() => void connect()}>{busy ? text.openingStripe : status?.has_account ? text.locationReviewInStripe : text.locationConnect}</button>{' '}
        </>}
        <button type="button" onClick={() => void query.refetch()} disabled={query.isFetching}>{text.refreshStatus}</button>
        {status?.checked_at && <p className={styles.muted}>{fillPayoutCopy(text.lastChecked, { time: new Date(status.checked_at).toLocaleString() })}</p>}
        {error && <p role="alert" className={styles.error}>{error}</p>}
      </>}
    </>}
  </section>;
}
